import React from "react";
import AsyncSelect from "react-select/async";
import { components, GroupBase, StylesConfig } from "react-select";
import {
	DropdownData,
	Mentionable,
	MentionTypes,
} from "../../../../common/sesh_types";
import { Constants } from "../../../../common/constants";
import chroma from "chroma-js";

interface SearchableDropdownProps {
	initial_mentions: Mentionable[];
	options: Mentionable[];
	longest_name_length?: number;
	handleChange: (mentionables: Mentionable[]) => void;
	placeholder?: string;
	ignore_invalid_permissions?: boolean;
	check_can_sesh_and_user_add_role?: boolean;
	is_single_select?: boolean;
	disabled?: boolean;
	loading?: boolean;
	ids_to_ignore?: string[];
	ids_to_ignore_text?: string;
	limit?: number;
}

interface SearchableDropdownState {
	mentions: DropdownData[];
	cached_search_results: Map<string, DropdownData[]>;
	useless_string_prefix_cache: Map<string, boolean>;
}

const ellipsis_option: DropdownData = {
	value: null,
	isDisabled: true,
	label: (
		<p
			className="has-text-weight-bold"
			style={{
				color: Constants.EMBED_COLOR,
				textAlign: "center",
				fontSize: "0.75rem",
			}}
		>
			More results than can be displayed. Type to filter.
		</p>
	),
};

export const SingleValue = (props) => (
	<components.SingleValue {...props}>
		{props.data.selectedLabel ?? props.data.label}
	</components.SingleValue>
);

export const dropdownColourStyles: StylesConfig<
	any,
	boolean,
	GroupBase<any>
> = {
	multiValue: (styles, { data }) => {
		let color = chroma(data.color ? data.color : "#000000");
		color =
			chroma.contrast(color, "black") > 2 ? color : color.darken(-1.5);
		return {
			...styles,
			backgroundColor: color.alpha(0.1).css(),
		} as any;
	},
	multiValueLabel: (styles, { data }) => {
		let color = chroma(data.color ? data.color : "#000000");
		color =
			chroma.contrast(color, "black") > 2 ? color : color.darken(-1.5);
		return {
			...styles,
			color: color.css(),
		} as any;
	},
	multiValueRemove: (styles, { data }) => {
		let color = chroma(data.color ? data.color : "#000000");
		color =
			chroma.contrast(color, "black") > 2 ? color : color.darken(-1.5);

		let css_color = color.css();
		return {
			...styles,
			color: css_color,
			":hover": {
				backgroundColor: css_color,
				color: "black",
			},
		} as any;
	},
};

export function DropdownMemberSelectedLabel(props: { val: Mentionable }) {
	const { val } = props;

	return (
		<div className="columns is-variable is-1 is-mobile" title={val.name}>
			<div className="column">
				<div className="columns is-vcentered is-variable is-1 is-mobile">
					<div className="column is-narrow" />
					<div className="column is-narrow">
						{val.avatar_url && (
							<figure className="image is-16x16">
								<img
									className="is-rounded"
									src={
										"https://cdn.discordapp.com/" +
										val.avatar_url
									}
									width={16}
									height={16}
								/>
							</figure>
						)}
					</div>
					<div className="column">
						<p className="dropdown-name">{val.name}</p>
					</div>
				</div>
			</div>
		</div>
	);
}

export function DropdownMemberLabel(props: {
	val: Mentionable;
	disabled?: {
		class: string;
		text_element: JSX.Element | string;
	};
}) {
	const { val, disabled } = props;

	return (
		<div className="columns is-variable is-1 is-mobile" title={val.name}>
			<div className="column">
				<div className="columns is-vcentered is-variable is-1 is-mobile">
					<div className="column is-narrow" />
					<div className="column is-narrow">
						{val.avatar_url && (
							<figure className="image is-16x16">
								<img
									className="is-rounded"
									src={
										"https://cdn.discordapp.com/" +
										val.avatar_url
									}
									width={16}
									height={16}
								/>
							</figure>
						)}
					</div>
					<div className="column">
						<p className="dropdown-name">{val.name}</p>
					</div>
					{disabled && (
						<div>
							<div
								className={`column is-pulled-right ${disabled.class}`}
							>
								{disabled.text_element}
							</div>
							<div className="column is-narrow" />
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export class SearchableDropdown extends React.Component<
	SearchableDropdownProps,
	SearchableDropdownState
> {
	constructor(props) {
		super(props);

		let initial_dropdown_data: DropdownData[] = [];
		for (let mention of this.props.initial_mentions) {
			initial_dropdown_data.push(this.createDropdownElement(mention));
		}

		this.state = {
			mentions: initial_dropdown_data ?? [],
			cached_search_results: new Map<string, DropdownData[]>(),
			useless_string_prefix_cache: new Map<string, boolean>(),
		};

		this.handleChange = this.handleChange.bind(this);
		this.createDropdownElement = this.createDropdownElement.bind(this);
	}

	componentDidUpdate(prevProps: SearchableDropdownProps) {
		let is_equal = Boolean(prevProps);

		if (is_equal) {
			is_equal =
				prevProps.initial_mentions.length ===
				this.props.initial_mentions.length;

			if (is_equal) {
				for (let i = 0; i < this.props.initial_mentions.length; i++) {
					if (
						prevProps.initial_mentions[i].id !==
						this.props.initial_mentions[i].id
					) {
						is_equal = false;
						break;
					}
				}
			}
		}

		if (!is_equal) {
			this.setState({
				mentions: this.props.initial_mentions.map((val) =>
					this.createDropdownElement(val)
				),
			});
		}
	}

	createDropdownElement(val: Mentionable): DropdownData {
		let color = "#ffffff";
		if (val.type === MentionTypes.Role || val.is_everyone_or_here) {
			color = val.color;
			if (color === "#000000") {
				color = "#ffffff";
			}
		}
		let disabled = false;
		let disabled_text_element: JSX.Element;
		let disabled_class = "dropdown-disabled-text";
		if (val.disabled && !this.props.ignore_invalid_permissions) {
			disabled = true;
			disabled_text_element = <span>Invalid Permissions</span>;
		} else if (
			this.props.ids_to_ignore &&
			this.props.ids_to_ignore.includes(val.id)
		) {
			disabled = true;
			disabled_text_element = (
				<span>{this.props.ids_to_ignore_text}</span>
			);
		}

		if (
			!this.props.disabled &&
			this.props.check_can_sesh_and_user_add_role
		) {
			if (!val.can_sesh_add_role || !val.can_user_add_role) {
				disabled_class += " dropdown-disabled-add-role";
				disabled = true;

				let disabled_reasons: string[] = [];
				if (!val.can_sesh_add_role) {
					disabled_reasons.push("sesh");
				}
				if (!val.can_user_add_role) {
					disabled_reasons.push("User");
				}
				disabled_text_element = (
					<a
						className="manual-link"
						href={
							Constants.ATTENDEE_ROLES_HELP_LINK +
							"#invalid_permissions"
						}
						target="_blank"
					>
						Invalid {disabled_reasons.join(" & ")} permissions
						<i className="fas fa-external-link-alt ml-[0.4em]"></i>
					</a>
				);
			}
		}

		return {
			value: val.id,
			isDisabled: disabled,
			mentionable: val,
			color,
			selectedLabel: <DropdownMemberSelectedLabel val={val} />,
			label: (
				<DropdownMemberLabel
					val={val}
					disabled={
						disabled
							? {
									class: disabled_class,
									text_element: disabled_text_element,
							  }
							: undefined
					}
				/>
			),
		};
	}

	loadOptions(
		options: Mentionable[],
		input_value: string,
		callback: Function
	) {
		if (this.props.loading) {
			return;
		}

		if (
			this.props.longest_name_length &&
			input_value.length > this.props.longest_name_length
		) {
			callback([]);
			return;
		}

		if (this.state.cached_search_results.has(input_value)) {
			let value = this.state.cached_search_results.get(input_value);
			this.state.cached_search_results.delete(input_value);
			this.state.cached_search_results.set(input_value, value);
			callback(value);
			return;
		}

		const input_value_lowercase = input_value.toLowerCase();

		let useless_cache_key;
		this.state.useless_string_prefix_cache.forEach((_, key) => {
			if (input_value_lowercase.startsWith(key)) {
				useless_cache_key = key;
				return;
			}
		});

		if (useless_cache_key) {
			this.state.useless_string_prefix_cache.delete(useless_cache_key);
			this.state.useless_string_prefix_cache.set(useless_cache_key, true);
			callback([]);
			return;
		}

		let result: DropdownData[] = [];
		let count = 0;
		for (let option of options) {
			let name = option.name.toLowerCase();
			if (name.includes(input_value_lowercase)) {
				let option_element = this.createDropdownElement(option);
				result.push(option_element);
				count++;
				if (count > Constants.WEB_UI_DROPDOWN_LIMIT) {
					break;
				}
			}
		}

		if (count === 0) {
			this.state.useless_string_prefix_cache.set(
				input_value_lowercase,
				true
			);

			if (
				this.state.useless_string_prefix_cache.size >
				Constants.WEB_UI_DROPDOWN_FORM_CACHE_SIZE
			) {
				const keyToDelete = this.state.useless_string_prefix_cache
					.keys()
					.next().value;
				this.state.useless_string_prefix_cache.delete(keyToDelete);
			}
		} else {
			this.state.cached_search_results.set(input_value_lowercase, result);

			if (
				this.state.cached_search_results.size >
				Constants.WEB_UI_DROPDOWN_FORM_CACHE_SIZE
			) {
				const keyToDelete = this.state.cached_search_results
					.keys()
					.next().value;
				this.state.cached_search_results.delete(keyToDelete);
			}

			if (count > Constants.WEB_UI_DROPDOWN_LIMIT) {
				result.push(ellipsis_option);
			}
		}

		callback(result);
	}

	handleChange(event: DropdownData[]) {
		if (this.props.disabled) {
			return;
		}

		if (!event) {
			event = [];
		}

		if (!Array.isArray(event)) {
			event = [event];
		}

		if (this.props.limit && event.length > this.props.limit) {
			return;
		}

		event = event.filter((val) => Boolean(val.mentionable));

		this.setState({ mentions: event });

		let mentionables = event.map((val) => val.mentionable);

		this.props.handleChange(mentionables);
	}

	render() {
		const limit_reached =
			this.props.limit && this.state.mentions.length >= this.props.limit;

		return (
			<div>
				{limit_reached && (
					<div className="error-text">
						Selection limit reached ({this.props.limit} /{" "}
						{this.props.limit})
					</div>
				)}
				<AsyncSelect
					isClearable
					classNamePrefix="sesh-dropdown"
					value={this.state.mentions}
					styles={dropdownColourStyles}
					onChange={this.handleChange}
					isMulti={!Boolean(this.props.is_single_select)}
					defaultOptions={true}
					loadOptions={(input_value, callback) =>
						this.loadOptions(
							this.props.options,
							input_value,
							callback
						)
					}
					isDisabled={this.props.disabled || this.props.loading}
					placeholder={
						!this.props.loading
							? this.props.placeholder
							: "Loading..."
					}
					isLoading={this.props.loading}
					components={{ SingleValue }}
				/>
			</div>
		);
	}
}
